/* eslint-disable no-console */
import { AnimatePresence, motion } from 'framer-motion';
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FiX } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useLocalStorage } from 'react-use';

import { BoxGoPremium } from '../../components/BoxGoPremium';
import { DetailsPlayerSubmit } from '../../components/Team/DetailsPlayer';
import { HeaderMyTeam } from '../../components/Team/HeaderMyTeam';
import { useBalance } from '../../hooks/useBalance';

import { Layout } from './components/Layout';
import { useIsBeforeVeda } from './hooks/useIsBeforeVeda';

import { Loader, Modal, NavBar } from '@/components';
import { Alert } from '@/components/Alert';
import { FieldAssistant } from '@/components/FieldAssistant';
import { AdSlot } from '@/components/GoogleAds/AdSlot';
import { FieldLayout } from '@/components/Layouts/FieldLayoutF5';
import { PinsDiscountLegend } from '@/components/PinsDiscountLegend';
import { DefaultTabsProps, Tabs } from '@/components/Tabs';
import { BoxNewsTeam, BoxRankingTeam, BoxTshirtEdit } from '@/components/Team';
import { DetailsPlayer } from '@/components/Team/DetailsPlayer';
import { FormFilterPlayers } from '@/components/Team/FormFilterPlayers';
import { ModalChangeConfirm } from '@/components/Team/ModalChangeConfirm';
import { ModalChangeSuggestion } from '@/components/Team/ModalChangeSuggestion';
import { ModalWhatsPins } from '@/components/Team/ModalPinsF5';
import { DesktopSidebar, MobileSidebar } from '@/components/Team/Sidebar';
import { TabType } from '@/components/Types';
import useAuth from '@/hooks/useAuth';
import useCheckPublicacion from '@/hooks/useCheckPublicacion';
import { useGoogleAds } from '@/hooks/useGoogleAds';
import { useHandleChangeCaptain } from '@/hooks/useHandleChangeCaptain';
import { useHandleChangeInsured } from '@/hooks/useHandleChangeInsured';
import { useHandleChangesTabs } from '@/hooks/useHandleChangesTabs';
import { useHandleTransfer } from '@/hooks/useHandleTransfer';
import useHostbackButton, {
  useUndepsHostbackButton,
} from '@/hooks/useHostbackButton';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { useMetaData } from '@/hooks/useMetaData';
import { useGetCurrentTeamF5, usePostDataRanking } from '@/hooks/useMyTeam5';
import usePinsAccount from '@/hooks/usePinsAccount';
import { usePlayerClub } from '@/hooks/usePlayer';
import { useTeam } from '@/hooks/useTeam5';
import { useTeamStore } from '@/hooks/useTeamStore';
import useToast from '@/hooks/useToast';
import { usePersistListFilters } from '@/modules/my-team/hooks/usePersistListFilters';
import ROUTES from '@/router/routes';
import { TeamActionTypes } from '@/store/team';
import { EnumDevice } from '@/types';
import {
  PlayerClub,
  EnumTeamContext,
  TemplatePlayer,
  PlayerF5,
  PlayerHistoric,
} from '@/types/team';
import { PlayerMyTeamF5, ResponseNewCaptainF5 } from '@/types/teamf5';
import {
  MAX_INPUT_RANGE,
  MIN_INPUT_RANGE,
  SLIDE_IN_OUT_LEFT,
  lockScroll,
} from '@/utils';
import { ADS_DIV_ID, SECTION_NAME_FOR_ADS } from '@/utils/ads';

export const Team5 = () => {
  const { tab, handleTabChanges } = useHandleChangesTabs();
  useMetaData({
    title: 'Mi Equipo de Fútbol 5',
  });
  const toast = useToast();
  const { isDesktop, isMobile } = useIsDesktop();
  const navigate = useNavigate();
  const { userInitialData } = useAuth();
  useCheckPublicacion('5');
  const { state, dispatch } = useTeamStore();
  const { pinsData, handleSetPinsData } = usePinsAccount();

  useGoogleAds({
    adsSections: SECTION_NAME_FOR_ADS.futbol5,
  });

  useEffect(() => {
    // Si no tengo equipo, redirijo a crear equipo
    // (slider/popup te pueden traer hasta acá, por eso se debe controlar)
    if (!userInitialData?.tiene_equipo_f5) {
      navigate(ROUTES.welcomeF5);
    }
  }, [navigate, userInitialData]);

  // pedido inicial de mi equipo
  const {
    data: getCurrentTeamData,
    isFetching: isFetchingCurrentTeam,
    refetch,
  } = useGetCurrentTeamF5(userInitialData?.tiene_equipo_f5);

  const {
    mutate: postDataRanking,
    data: responseRankingHistoric,
    isLoading: isLoadingRankingHistoric,
    error: errorRankingHistoric,
  } = usePostDataRanking();

  const { handleTransferSubmit, isLoadingTransfer, responseTransfer } =
    useHandleTransfer(true); // boolean if F5

  const {
    handleNewCaptain,
    responseNewCaptain,
    isLoadingNewCaptain,
    updateCaptain,
  } = useHandleChangeCaptain(true); // boolean if F5

  const {
    handleNewInsured,
    isLoadingNewInsured,
    responseNewInsured,
    updateInsured,
  } = useHandleChangeInsured();

  const isBeforeVeda = useIsBeforeVeda(userInitialData?.comienzo_veda || '');
  const { freezeListPosition, getListFiltersToRestore } =
    usePersistListFilters();

  useEffect(() => {
    dispatch({
      type: TeamActionTypes.SET_STATE,
      payload: {
        isBeforeVeda,
      },
    });
  }, [dispatch, isBeforeVeda]);

  const [isSuggestionOpen, setIsSuggestionOpen] = useState(false);
  const [suggestionSessionValue, setSuggestionSessionValue] =
    useLocalStorage<boolean>(
      `suggestion-${EnumTeamContext.Team5}-${userInitialData?.idUsuarioDT}`,
      true
    );

  const changeSuggestionValue = useCallback(() => {
    setSuggestionSessionValue((prevValue) => !prevValue);
  }, [setSuggestionSessionValue]);

  const openSuggestionModal = useCallback(() => setIsSuggestionOpen(true), []);
  const closeSuggestionModal = useCallback(
    () => setIsSuggestionOpen(false),
    []
  );

  const isTransitioningRef = useRef(false);
  const transitionTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const alertInfoSuggestions = useMemo(() => {
    return (
      <Alert
        className="bg-gdt-blue max-w-[38rem] w-full lg:mt-5   "
        message={
          <div className="flex flex-wrap">
            {getCurrentTeamData?.estado &&
            getCurrentTeamData.arraySugerenciaTO &&
            getCurrentTeamData.arraySugerenciaTO.every(
              (player) => player.idGrupo === 1
            ) ? (
              <p>
                Tenés futbolistas que no jugarían la{' '}
                <span className="whitespace-nowrap me-1">
                  {userInitialData?.fechaActiva.nombre}.{' '}
                </span>
              </p>
            ) : (
              <span className="me-1">
                Hay futbolistas que no te vienen rindiendo.{' '}
              </span>
            )}
            <strong
              className="cursor-pointer flex-auto whitespace-nowrap"
              onClick={openSuggestionModal}
            >
              Ver Sugerencias
            </strong>
          </div>
        }
        variant={isMobile ? EnumDevice.Mobile : EnumDevice.Desktop}
        onCloseAction={changeSuggestionValue}
      />
    );
  }, [
    getCurrentTeamData,
    userInitialData?.fechaActiva.nombre,
    isMobile,
    setSuggestionSessionValue,
    changeSuggestionValue,
    openSuggestionModal,
  ]);

  const showSuggestiveMessage = () => {
    return (
      getCurrentTeamData?.estado &&
      getCurrentTeamData.arraySugerenciaTO &&
      getCurrentTeamData.arraySugerenciaTO.length !== 0 &&
      suggestionSessionValue &&
      alertInfoSuggestions
    );
  };

  const { getPlayersOnField } = useTeam();
  const { resolveTeamPlayerData } = usePlayerClub();
  const { setTeamBalance } = useBalance();

  // keepListFilters: false en montaje/desmontaje para inicializar los filtros de la seccion (DTA-10108)
  const handleResetActions = (keepListFilters = true) => {
    dispatch({
      type: TeamActionTypes.SET_STATE,
      payload: {
        selectedPlayerOnList: null,
        selectedPlayerOnTeam: null,
        showPlayersList: false,
        showFiltersPlayersList: false,
        showModalChangePlayer: false,
        playerDetailsModalOpen: false,
        ...((keepListFilters && getListFiltersToRestore()) || {
          selectedPosition: null,
          filtersApplied: {
            intoBudget: false,
            orderBy: null,
            orderClub: null,
            valuesRange: [MIN_INPUT_RANGE, MAX_INPUT_RANGE],
            positionF5: null,
          },
        }),
      },
    });
    state.teamF5 && handleSetTeamOnField(state.teamF5);
  };

  // fijo el scroll cuando abro filtros, lista de jugadores o listado de transferencia desde ayudante
  useEffect(() => {
    lockScroll(
      Boolean(
        isMobile &&
          (state.showFiltersPlayersList ||
            state.showPlayersList ||
            state.showPlayersListTransfer)
      )
    );

    return () => {
      lockScroll();
    };
  }, [
    state.showPlayersListTransfer,
    state.showFiltersPlayersList,
    state.showPlayersList,
    isMobile,
  ]);

  // hago scroll al top de pagina cuando abro modal de detalle de jugador
  useEffect(() => {
    if (
      state.selectedPlayerOnTeam &&
      !state.showPlayersListTransfer &&
      isDesktop &&
      !state.showPlayersList
    ) {
      scrollTo(0, 265);
    }
  }, [
    state.selectedPlayerOnTeam,
    state.showPlayersListTransfer,
    isDesktop,
    state.showPlayersList,
  ]);

  // Hago una nueva llamada al servicio de equipo actual para actualizar data de equipo post transferencia. Tema pines
  useEffect(() => {
    if (
      responseTransfer?.estado ||
      pinsData.pinsAvailable !== userInitialData?.pinesDisponibles
    ) {
      refetch();
    }
    if (responseTransfer?.pinesDisponibles !== undefined) {
      handleSetPinsData({
        ...pinsData,
        pinsAvailable: responseTransfer?.pinesDisponibles,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responseTransfer, pinsData.pinsAvailable]);

  // limpia state en acciones de back, close o post submit data. Dependiendo
  useEffect(() => {
    return () => {
      handleResetActions(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresco el estado cuando refresco estado de equipo
  const isTeamLoadedRef = useRef(false);
  useEffect(() => {
    handleResetActions(isTeamLoadedRef.current);
    isTeamLoadedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.teamF5]);

  // efecto maneja tabs
  useEffect(() => {
    if (tab === TabType.FirstTab) {
      if (
        getCurrentTeamData?.estado &&
        getCurrentTeamData?.equipo_f5?.jugadores
      ) {
        dispatch({
          type: TeamActionTypes.SET_STATE,
          payload: {
            responseTeamF5:
              updateCaptain.isUpdateCaptain || updateInsured.isUpdateInsured
                ? {
                    ...getCurrentTeamData,
                    equipo_f5: {
                      ...getCurrentTeamData.equipo_f5,
                      jugadores: getCurrentTeamData.equipo_f5.jugadores.map(
                        (jugador) => {
                          const updatedPlayer = { ...jugador };

                          if (updateCaptain.isUpdateCaptain) {
                            updatedPlayer.capitan =
                              jugador.id === updateCaptain.id;
                          }

                          if (updateInsured.isUpdateInsured) {
                            updatedPlayer.seguro =
                              jugador.id === updateInsured.id;
                          }

                          return updatedPlayer;
                        }
                      ),
                    },
                  }
                : getCurrentTeamData,
          },
        });

        const infoTeam = resolveTeamPlayerData(
          getCurrentTeamData?.equipo_f5?.jugadores
        )?.map((player: any) => player?.cotizacion);

        const balance =
          infoTeam &&
          infoTeam?.reduce((prev = 0, curr = 0) => {
            return prev + curr;
          });
        handleSetTeamOnField(
          updateCaptain.isUpdateCaptain || updateInsured.isUpdateInsured
            ? getCurrentTeamData.equipo_f5.jugadores.map((jugador) => {
                const updatedPlayer = { ...jugador };

                if (updateCaptain.isUpdateCaptain) {
                  updatedPlayer.capitan = jugador.id === updateCaptain.id;
                }

                if (updateInsured.isUpdateInsured) {
                  updatedPlayer.seguro = jugador.id === updateInsured.id;
                }

                return updatedPlayer;
              })
            : getCurrentTeamData.equipo_f5.jugadores
        );
        balance && setTeamBalance(balance);
      }
    } else if (tab === TabType.SecondTab && responseRankingHistoric) {
      const teamHistoric = responseRankingHistoric.jugadores.map(
        (p: PlayerHistoric) => {
          return {
            orden: p.orden,
            id: p.jugador,
            titular: p.titular,
            capitan: p.capitan,
            seguro: p.seguro,
            puntaje: p.puntaje,
          } as PlayerHistoric;
        }
      );

      const infoTeam = resolveTeamPlayerData(teamHistoric)?.map((player) =>
        'cotizacion' in player ? player?.cotizacion : 0
      );

      const balance =
        infoTeam &&
        infoTeam?.reduce((prev = 0, curr = 0) => {
          return prev + curr;
        });

      teamHistoric && handleSetTeamOnField(teamHistoric);
      balance && setTeamBalance(balance);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, responseRankingHistoric, getCurrentTeamData, tab]);

  // manejo el error de ranking y voy al primer tab
  useEffect(() => {
    if (errorRankingHistoric) {
      handleTabChanges(TabType.FirstTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorRankingHistoric]);

  // MANEJO DE RESPUESTA DE TRANSFERENCIA
  useEffect(() => {
    if (responseTransfer) {
      if (
        responseTransfer?.estado &&
        'equipo_f5' in responseTransfer &&
        responseTransfer?.equipo_f5?.jugadores
      ) {
        const isNewCaptain =
          state.teamF5?.find(
            (player) => player.id === state.selectedPlayerOnTeam?.id
          )?.capitan || false;

        const isNewInsured =
          state.teamF5?.find(
            (player) => player.id === state.selectedPlayerOnTeam?.id
          )?.seguro || false;

        const messageIsCaptain =
          isNewCaptain &&
          !isNewInsured &&
          `Tu nuevo capitán es ${
            state.selectedPlayerOnList?.a
          } ${state.selectedPlayerOnList?.n.at(0)}.`;

        const messageIsInsured =
          isNewInsured &&
          !isNewCaptain &&
          `Tu nuevo asegurado es ${
            state.selectedPlayerOnList?.a
          } ${state.selectedPlayerOnList?.n.at(0)}.`;

        const messageBoth =
          isNewInsured &&
          isNewCaptain &&
          `Tu nuevo capitán y asegurado es ${
            state.selectedPlayerOnList?.a
          } ${state.selectedPlayerOnList?.n.at(0)}.`;

        const messageDefault =
          !isNewInsured &&
          !isNewCaptain &&
          `${state.selectedPlayerOnList?.a} ${state.selectedPlayerOnList?.n.at(
            0
          )} ahora forma parte de tu equipo. `;

        // transferencia con exito
        toast.show(
          <>
            <strong>Transferencia realizada.</strong>{' '}
            {messageIsCaptain ||
              messageIsInsured ||
              messageBoth ||
              messageDefault}
          </>
        );

        // Redirijo a mi equipo: cierro la lista de jugadores y modal
        dispatch({
          type: TeamActionTypes.SET_STATE,
          payload: {
            showPlayersList: false,
            playerDetailsModalOpen: false,
            selectedPlayerOnList: null,
            selectedPlayerOnTeam: null,
          },
        });
        // Refresco equipo en cancha
        handleSetTeamOnField(responseTransfer.equipo_f5.jugadores);
      } else {
        if (!responseTransfer?.requiere_pin) {
          toast.show(responseTransfer?.mensaje);
        } else {
          handleSetPinsData({
            ...pinsData,
            showModalWhatsPins: true,
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responseTransfer]);

  // MANEJO DE RESPUESTA DE NUEVO ASEGURADO
  useEffect(() => {
    if (
      responseNewInsured?.estado &&
      responseNewInsured?.equipo_f5?.jugadores
    ) {
      const newIsured = userInitialData?.plantilla.jugadores?.find(
        (player) => player.id === state.selectedPlayerOnTeam?.id
      );

      // cambio con exito
      toast.show(
        `Tu nuevo asegurado es ${newIsured?.a} ${newIsured?.n.at(0)}.`
      );

      // Redirijo a mi equipo: cierro la lista de jugadores y modal
      dispatch({
        type: TeamActionTypes.SET_STATE,
        payload: {
          playerDetailsModalOpen: false,
          selectedPlayerOnTeam: null,
        },
      });
      // Refresco equipo en cancha
      handleSetTeamOnField(responseNewInsured.equipo_f5.jugadores);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responseNewInsured]);

  // Type guard function to check which type of response we have
  const isResponseNewCaptain = (data: any): data is ResponseNewCaptainF5 => {
    // Add a property check that's unique to ResponseNewCaptain
    return data;
  };

  // MANEJO DE RESPUESTA DE NUEVO CAPITAN
  useEffect(() => {
    if (responseNewCaptain?.estado) {
      const newCaptain = userInitialData?.plantilla.jugadores?.find(
        (player) => player.id === state.selectedPlayerOnTeam?.id
      );

      // cambio con exito
      toast.show(
        `Tu nuevo capitán es ${newCaptain?.a} ${newCaptain?.n.at(0)}.`
      );

      // Redirijo a mi equipo: cierro la lista de jugadores y modal
      dispatch({
        type: TeamActionTypes.SET_STATE,
        payload: {
          playerDetailsModalOpen: false,
          selectedPlayerOnTeam: null,
        },
      });

      const team: PlayerMyTeamF5[] | undefined = isResponseNewCaptain(
        responseNewCaptain
      )
        ? responseNewCaptain?.equipo_f5?.jugadores
        : [];

      // Refresco equipo en cancha
      team && handleSetTeamOnField(team);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responseNewCaptain]);

  // Resetea el modal de pines cuando cambio hacia atras en la vista
  useUndepsHostbackButton(() => {
    handleSetPinsData({
      ...pinsData,
      showInputsModalPins: false,
      showModalWhatsPins: false,
    });
  });

  const handleBackButton = useCallback(() => {
    dispatch({
      type: TeamActionTypes.SET_STATE,
      payload: {
        ...(!state.showFiltersPlayersList
          ? {
              showPlayersList: false,
            }
          : { showFiltersPlayersList: false }),
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.showFiltersPlayersList]);
  useHostbackButton(handleBackButton);

  const handleSelectPlayer = (
    player: TemplatePlayer | PlayerF5 | PlayerMyTeamF5
  ) => {
    if ('posicion' in player) freezeListPosition(player.posicion?.nombreCorto);
    dispatch({
      type: TeamActionTypes.SET_STATE,
      payload: {
        ...('posicion' in player
          ? {
              selectedPosition: player.posicion?.nombreCorto,
              selectedPlayerOnTeam: player as TemplatePlayer & PlayerClub,
            }
          : {
              selectedPlayerOnList: player as TemplatePlayer,
            }),
        playerDetailsModalOpen: true,
      },
    });
  };

  // Esta funcion se encarga de dibujar los jugadores en la cancha
  const handleSetTeamOnField = (team: PlayerMyTeamF5[]) => {
    const players = getPlayersOnField({
      team,
      handleSelectPlayer,
      context:
        tab === TabType.FirstTab
          ? EnumTeamContext.Team5
          : EnumTeamContext.Score5,
    });

    const data = {
      type: TeamActionTypes.SET_STATE,
      payload: {
        teamF5: team,
        playersOnFieldF5: {
          goalkeeper: players.goalkeeper,
          fieldPlayers: players.fieldPlayers,
        },
      },
    };

    dispatch(data);

    return data.payload.playersOnFieldF5;
  };

  const tShirtCard = (
    <BoxTshirtEdit
      infoDt={{
        name: `${userInitialData?.nombre_dt}`,
        nameTeam: `${userInitialData?.nombre_equipo}`,
      }}
    />
  );

  const handleDetailsModal = useCallback(
    (data: DetailsPlayerSubmit) => {
      // https://agea.atlassian.net/browse/DTA-9806
      if (!state.selectedPlayerOnTeam) {
        toast.show('Primero elegí al jugador saliente');

        dispatch({
          type: TeamActionTypes.SET_STATE,
          payload: {
            playerDetailsModalOpen: false,
          },
        });
        return;
      }

      if (isTransitioningRef.current) return;

      if (data.willBeCaptain) {
        handleNewCaptain(data.player);
        return;
      }

      if (data.isInsuranced) {
        handleNewInsured(data.player);
        return;
      }

      if (!state.showPlayersList) {
        isTransitioningRef.current = true;

        // Timeout de seguridad: si onAfterClose no se ejecuta en 500ms, forzar
        if (transitionTimeoutRef.current) {
          clearTimeout(transitionTimeoutRef.current);
        }
        transitionTimeoutRef.current = setTimeout(() => {
          if (isTransitioningRef.current) {
            isTransitioningRef.current = false;
            if (state.selectedPlayerOnTeam) {
              dispatch({
                type: TeamActionTypes.SET_STATE,
                payload: {
                  showPlayersList: true,
                  playerDetailsModalOpen: false,
                },
              });
            }
          }
        }, 500);

        dispatch({
          type: TeamActionTypes.SET_STATE,
          payload: {
            selectedPlayerOnTeam: data.player,
            ...(isMobile && {
              selectedPlayerOnList: data.player,
            }),
            playerDetailsModalOpen: false,
          },
        });
      } else {
        handleTransferSubmit(data);
      }

      return null;
    },
    [
      dispatch,
      handleNewCaptain,
      handleNewInsured,
      handleTransferSubmit,
      isMobile,
      state.selectedPlayerOnTeam,
      state.showPlayersList,
      toast,
    ]
  );

  const rankingCard = useCallback(() => {
    if (getCurrentTeamData?.estado) {
      //  id_ult_fecha_puntos_f5 es null si usuario no tiene puntos.
      // infoPuntajes?.idFecha es null sólo si no hay ninguna publicación de puntos F5.
      // => ¿currentDateIsPublicated significa "usuario tiene ranking F5"?.
      const currentDateIsPublicated =
        getCurrentTeamData.infoPuntajes?.idFecha ===
        getCurrentTeamData?.id_ult_fecha_puntos_f5;
      const dataCurrentMatchDay = {
        dateName: userInitialData?.fechaMostrarRankingF5.nombre,
        ...(currentDateIsPublicated && {
          matchDayScore: getCurrentTeamData?.infoPuntajes?.puntosFecha,
        }),
        gralScore: getCurrentTeamData?.infoPuntajes?.puntosGeneral,
        ...(currentDateIsPublicated && {
          matchDayPosition: getCurrentTeamData?.infoPuntajes?.posicionFecha,
        }),
        gralPosition: getCurrentTeamData?.infoPuntajes?.posicionGeneral,
      };

      return (
        <BoxRankingTeam
          context={
            tab === TabType.FirstTab
              ? EnumTeamContext.Team5
              : EnumTeamContext.Score5
          }
          isLoading={isLoadingRankingHistoric}
          lastRanking={{
            dateName: dataCurrentMatchDay.dateName,
            matchDayScore: dataCurrentMatchDay.matchDayScore,
            gralScore: dataCurrentMatchDay.gralScore,
            matchDayPosition: dataCurrentMatchDay.matchDayPosition,
            gralPosition: dataCurrentMatchDay.gralPosition,
          }}
          matchsDaysAvailables={
            userInitialData?.fechas_disponibles_f5_obj || []
          }
          responseRanking={responseRankingHistoric || null}
          onSubmit={(date: string) => {
            postDataRanking(date);
          }}
        />
      );
    }
    // eslint-disable-next-line prettier/prettier, react-hooks/exhaustive-deps
  }, [
    getCurrentTeamData,
    tab,
    isLoadingRankingHistoric,
    responseRankingHistoric,
    userInitialData,
  ]);

  const arrTabs: DefaultTabsProps['tabs'] = useMemo(() => {
    return [
      { name: 'Equipo' },
      {
        name: 'Puntajes',
        disabled: userInitialData?.fechas_disponibles_f5.length === 0,
      },
    ];
  }, [userInitialData]);

  return (
    <>
      <Layout
        main={() => {
          return (
            <>
              <section className="px-4 lg:px-0 overflow-hidden mb-5 lg:mb-0">
                {isMobile && (
                  <Tabs
                    activeTab={tab}
                    tabs={arrTabs}
                    onClick={(tab: TabType) => handleTabChanges(tab)}
                  />
                )}
                {isMobile && tab === TabType.SecondTab && rankingCard()}
                {((isMobile && tab === TabType.FirstTab) || isDesktop) && (
                  <div className="mt-4 lg:mt-0">
                    <HeaderMyTeam context={EnumTeamContext.Team5} />
                  </div>
                )}
                {isMobile && (
                  <div className="mt-4">{showSuggestiveMessage()}</div>
                )}
                <div className="relative mt-4">
                  {isDesktop && (
                    <div className="absolute w-full top-0 z-[2] flex items-center flex-col lg:top-5">
                      {
                        <Tabs
                          activeTab={tab}
                          tabs={arrTabs}
                          onClick={(tab: TabType) => handleTabChanges(tab)}
                        />
                      }
                      {isDesktop && showSuggestiveMessage()}
                    </div>
                  )}
                  {
                    <Loader
                      isVisible={
                        isFetchingCurrentTeam ||
                        isLoadingTransfer ||
                        isLoadingRankingHistoric ||
                        isLoadingNewCaptain ||
                        isLoadingNewInsured
                      }
                      timeout={
                        isLoadingTransfer ||
                        isLoadingRankingHistoric ||
                        isLoadingNewCaptain ||
                        isLoadingNewInsured
                      }
                    />
                  }
                  <FieldLayout.Main>
                    {/* Close btn backdrop */}
                    {state.showPlayersList &&
                    isDesktop &&
                    !state.playerDetailsModalOpen ? (
                      <button
                        className="p-2 absolute right-[-15px] top-[-107.5px] z-[4] hidden lg:block bg-opacity-30 bg-white scale-[.4] rounded-full"
                        type="button"
                        onClick={() => handleResetActions()}
                      >
                        <FiX className="w-[50px] h-[50px] text-white" />
                      </button>
                    ) : null}
                    <FieldLayout.Inner>
                      <FieldLayout.Field
                        backDropOnClick={() => {
                          handleResetActions();
                        }}
                        backDropOpen={Boolean(
                          state.selectedPlayerOnTeam &&
                            isDesktop &&
                            !state.playerDetailsModalOpen
                        )}
                        context={EnumTeamContext.Team5}
                        fieldPlayers={state.playersOnFieldF5?.fieldPlayers}
                        goalkeeper={state.playersOnFieldF5?.goalkeeper}
                        isLoading={false}
                        isMounted={isDesktop}
                        selectedPositionOrden={
                          (state.selectedPlayerOnTeam &&
                            state.selectedPlayerOnTeam.id) ||
                          undefined
                        }
                      />
                    </FieldLayout.Inner>
                  </FieldLayout.Main>
                </div>
                {/* {isMobile && tab === TabType.FirstTab && tShirtCard} */}
              </section>
              {/* // AYUDANTE DE CAMPO */}
              <div className="lg:mb-11">
                <FieldAssistant
                  context={EnumTeamContext.Team5}
                  handleTransferAssistante={handleTransferSubmit}
                />
              </div>
            </>
          );
        }}
        sidebar={() => {
          return (
            <>
              {(state.showPlayersList || state.isBeforeVeda) &&
                tab === TabType.FirstTab &&
                isDesktop && (
                  <motion.div
                    animate={'show'}
                    className="min-w-[360px] sticky top-0 z-[1] flex flex-col lg:h-screen bg-white"
                    initial="hide"
                    variants={SLIDE_IN_OUT_LEFT}
                  >
                    <DesktopSidebar
                      context={EnumTeamContext.FilterTeam5}
                      handleSelectPlayerOnList={handleSelectPlayer}
                      onClick={() => {
                        dispatch({
                          type: TeamActionTypes.SET_STATE,
                          payload: {
                            showFiltersPlayersList:
                              !state.showFiltersPlayersList,
                          },
                        });
                      }}
                    />
                  </motion.div>
                )}
              <section className="space-y-4">
                {isDesktop && (
                  <Fragment>
                    {tShirtCard}
                    {rankingCard()}
                    <AdSlot
                      className="mx-auto"
                      divId={ADS_DIV_ID.caja1}
                      variant="BOX"
                    />
                    {getCurrentTeamData?.estado &&
                      getCurrentTeamData.novedadDestacada?.descripcion && (
                        <BoxNewsTeam
                          description={
                            getCurrentTeamData.novedadDestacada.descripcion
                          }
                        />
                      )}
                    <AdSlot
                      className="mx-auto"
                      divId={ADS_DIV_ID.caja2}
                      variant="BOX"
                    />
                    <BoxGoPremium
                      className="pb-7"
                      context={EnumTeamContext.Team5}
                      showStar={false}
                    />
                    <AdSlot
                      className="mx-auto"
                      divId={ADS_DIV_ID.caja3}
                      variant="BOX"
                    />
                  </Fragment>
                )}
              </section>
            </>
          );
        }}
        title={`${userInitialData?.nombre_equipo}`}
        trackSection={SECTION_NAME_FOR_ADS.futbol5}
      />
      {/* Modal para detalle del jugador */}
      <Modal
        bottomSheetClassName="px-[16px]"
        className="px-[1.06rem] lg:px-11 py-10"
        closeOutside={state.showPlayersList}
        controlledOnClose={() => {
          if (isDesktop) {
            const isSelectedFromList = Boolean(state.selectedPlayerOnList?.id);
            dispatch({
              type: TeamActionTypes.SET_STATE,
              payload: {
                ...(isSelectedFromList
                  ? {
                      selectedPlayerOnList: null,
                    }
                  : {
                      selectedPlayerOnTeam: null,
                      ...getListFiltersToRestore(),
                    }),
                playerDetailsModalOpen: false,
              },
            });
          } else {
            dispatch({
              type: TeamActionTypes.SET_STATE,
              payload: {
                playerDetailsModalOpen: false,
                ...(!state.showPlayersList && { selectedPlayerOnTeam: null }),
              },
            });
          }
        }}
        isControlledOpen={Boolean(
          state.playerDetailsModalOpen && !state.showPlayersListTransfer
        )}
        render={() => {
          if (
            state.selectedPlayerOnTeam?.id ||
            state.selectedPlayerOnList?.id
          ) {
            return (
              <DetailsPlayer
                context={EnumTeamContext.Team5}
                messageBeforeActions={
                  state.selectedPlayerOnList &&
                  getCurrentTeamData?.estado &&
                  getCurrentTeamData.seLeDescontara1Pin && (
                    <PinsDiscountLegend />
                  )
                }
                playerId={
                  !state.showPlayersList && state.selectedPlayerOnTeam
                    ? state.selectedPlayerOnTeam?.id
                    : state.selectedPlayerOnList?.id
                }
                playersOnFieldF5={state.playersOnFieldF5}
                selectedPlayerOnField={state.selectedPlayerOnTeam}
                selectedPlayerOnList={state.selectedPlayerOnList}
                variant={
                  state.showPlayersList ||
                  (!state.selectedPlayerOnTeam && state.isBeforeVeda)
                    ? 'add'
                    : 'transfer'
                }
                onSubmitPlayer={handleDetailsModal}
              />
            );
          }
        }}
        size="xs"
        variant="controlled"
        onAfterClose={() => {
          // Limpiar el timeout si onAfterClose se ejecuta normalmente
          if (transitionTimeoutRef.current) {
            clearTimeout(transitionTimeoutRef.current);
            transitionTimeoutRef.current = null;
          }

          isTransitioningRef.current = false;
          if (state.selectedPlayerOnTeam) {
            dispatch({
              type: TeamActionTypes.SET_STATE,
              payload: { showPlayersList: true },
            });
          }
        }}
      />
      {/* SlideOver para seleccion de jugadores */}

      <AnimatePresence>
        {state.showPlayersList && isMobile && (
          <motion.nav
            animate="show"
            className="fixed top-0 w-full z-50"
            initial="hide"
            variants={SLIDE_IN_OUT_LEFT}
          >
            <MobileSidebar
              backAction={() => {
                dispatch({
                  type: TeamActionTypes.SET_STATE,
                  payload: {
                    selectedPlayerOnList: null,
                    selectedPlayerOnTeam: null,
                    selectedPosition: null,
                    showPlayersList: false,
                    filtersApplied: {
                      intoBudget: false,
                      orderBy: null,
                      orderClub: null,
                      valuesRange: [MIN_INPUT_RANGE, MAX_INPUT_RANGE],
                    },
                  },
                });
              }}
              context={EnumTeamContext.FilterTeam5}
              handleSelectPlayerOnList={handleSelectPlayer}
              title={`Transferir a ${state.selectedPlayerOnTeam?.a}`}
              onClick={() => {
                dispatch({
                  type: TeamActionTypes.SET_STATE,
                  payload: {
                    showFiltersPlayersList: true,
                  },
                });
              }}
            />
          </motion.nav>
        )}
      </AnimatePresence>
      {/* SlideOver para filtros */}
      <AnimatePresence>
        {state.showFiltersPlayersList && isMobile && (
          <motion.nav
            animate="show"
            className="fixed top-0 w-full z-50"
            initial="hide"
            variants={SLIDE_IN_OUT_LEFT}
          >
            <NavBar
              mobile={{
                rightContent: {
                  customRightAction: () => {
                    dispatch({
                      type: TeamActionTypes.SET_STATE,
                      payload: {
                        showFiltersPlayersList: false,
                        ...state.filtersApplied,
                      },
                    });
                  },
                },
                centerContent: {
                  title: `Filtros`,
                },
              }}
            >
              <FormFilterPlayers context={EnumTeamContext.Team5} />
            </NavBar>
          </motion.nav>
        )}
      </AnimatePresence>
      {/* modal  info pines */}
      <ModalWhatsPins
        showModal={
          (pinsData.showModalWhatsPins || pinsData.showInputsModalPins) &&
          Boolean(!state.showPlayersListTransfer)
        }
        onSubmitTransfer={handleTransferSubmit}
      />
      {/* modal confirmación de cambio */}
      {state.showModalChangePlayer && <ModalChangeConfirm />}
      {/* Modal para sugerir cambio de jugadores */}
      {isSuggestionOpen && (
        <ModalChangeSuggestion
          context={EnumTeamContext.Team5}
          isOpen={isSuggestionOpen}
          onClose={closeSuggestionModal}
        />
      )}

      {/* Modal para cambiar jugador sugerido */}
      <Modal
        bottomSheetClassName="px-[16px]"
        className="px-[1.06rem] lg:px-11 py-10"
        controlledOnClose={() => {
          dispatch({
            type: TeamActionTypes.SET_STATE,
            payload: {
              addToTeamBySuggestionModal: false,
              selectedPlayerOnList: null,
              selectedPlayerOnTeam: null,
            },
          });
        }}
        isControlledOpen={Boolean(state.addToTeamBySuggestionModal)}
        render={() => {
          if (state.selectedPlayerOnList?.id) {
            return (
              <DetailsPlayer
                context={EnumTeamContext.Team5}
                messageBeforeActions={
                  getCurrentTeamData?.estado &&
                  getCurrentTeamData?.seLeDescontara1Pin &&
                  state.selectedPlayerOnList && <PinsDiscountLegend />
                }
                playerId={state.selectedPlayerOnList?.id}
                playersOnFieldF5={state.playersOnFieldF5}
                selectedPlayerOnField={state.selectedPlayerOnTeam}
                selectedPlayerOnList={state.selectedPlayerOnList}
                variant={'add'}
                onSubmitPlayer={(data) => {
                  handleTransferSubmit(data);
                }}
              />
            );
          }
        }}
        size="xs"
        variant="controlled"
      />
    </>
  );
};
